/**
 * Guard for a public page that exists only while a feature toggle is on. Its only use is
 * `pages/easy-quote/index.tsx`, with `Login/EasyQuoteHelpers/EasyQuote.constants.ts` →
 * `EasyQuoteFeatureFlag`.
 *
 * The toggles come from `FeatureTogglesProvider` for `ApplicationName.DotCom`
 * (`RootLayout.tsx`), the same set web-store-ui reads. With `featureString` set:
 * - toggles not loaded yet (`featuresList` empty): a "Loading" block;
 * - toggle on: renders `component` with the remaining props;
 * - toggle off: leaves the page with `window.location.href`, to `"store/dashboard"` for a user
 *   with `Customer_Dashboard_View_Edit`, otherwise to `/`.
 * Without `featureString` it renders `component` unconditionally.
 *
 * Two behaviours a change should know about:
 * - `"store/dashboard"` has no leading slash, so the browser resolves it against the current
 *   page. Exported pages end in a slash (`next.config.js` → `trailingSlash`), so from
 *   `/easy-quote/` it goes to `/easy-quote/store/dashboard`, a path with no page. The Easy Quote
 *   page doesn't reach this today: `Login/hooks/useRedirectIfLoggedInEasyQuote.ts` sends a
 *   signed-in visitor to `/store/quotes/<quoteId>` before the guard renders, and a signed-out
 *   visitor has no sign-in token, so no permissions, and goes to `/`.
 * - An empty `featuresList` reads as "still loading". When the toggles request fails, the list
 *   stays empty and the page shows "Loading" for good. web-architecture ADR-0045 §2 requires a
 *   toggle failure to end in a visible outcome (an error, or the toggle-off behaviour), and this
 *   guard doesn't meet it yet: `@ryerson/frontend.feature-toggles` doesn't report a failure, so
 *   the guard can't tell "failed" from "loading".
 *
 * web-store-ui has its own copy:
 * `web-store-ui/application/src/components/NextLayouts/PublicWithFeature.tsx`, which navigates to
 * `/dashboard` under its `/store` base path.
 */
import { Permission } from "@ryerson/common.permissions";
import { useApplication } from "@ryerson/frontend.application";
import { useFeatureToggles } from "@ryerson/frontend.feature-toggles";
import { ContentSection, Flex } from "@ryerson/frontend.layout";
import { navigate } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import React, { FunctionComponent } from "react";

// Every prop other than `component` and `featureString` is passed through to `component`.
// `third` through `quantityUom` and `permission` are not read here, and the Easy Quote page
// passes none of them.
type RouteProps = {
	component?: FunctionComponent<any>;
	third?: string;
	form?: string;
	product?: string;
	quantity?: string;
	priceUom?: string;
	quantityUom?: string;
	permission?: Permission;
	// The toggle key, as `isFeatureEnabled` takes it. Omitted means no gate.
	featureString?: string;
};

/** Renders `component` only while `featureString` is enabled; see the file header. */
const PublicWithFeature: React.FC<Partial<RouteProps>> = ({
	component: Component,
	featureString,
	...rest
}) => {
	const { hasPermission } = useApplication();
	const { isFeatureEnabled, featuresList } = useFeatureToggles();

	if (!!featureString) {
		if (!!featuresList?.length) {
			if (isFeatureEnabled(featureString)) {
			 
				return Component ? <Component {...rest} /> : <></>;
			} else {
				// A full page load, so a `/store` URL is requested from CloudFront and served by
				// web-store-ui. The dashboard target is relative to the current page (see the
				// file header).
				hasPermission(Permission.Customer_Dashboard_View_Edit)
					? (window.location.href = "store/dashboard")
					: (window.location.href = "/");
				return null;
			}
		} else {
			// fall back display while feature is loading
			return (
				<ContentSection type="tertiary" data-section="search-input-section">
					<Flex css={{ padding: 40, height: 600 }}>
						<Typography type={"tertiary"}>Loading </Typography>
					</Flex>
				</ContentSection>
			);
		}
	} else {
		return Component ? <Component {...rest} /> : <></>;
	}
};

export default PublicWithFeature;
